import { useIntervalFn } from '@vueuse/core'

type UseVideoTrackingAdapter = {
  /**
   * Return the video title.
   */
  getTitle: () => string | undefined | Promise<string>

  /**
   * Return the curren time as seconds.
   */
  getCurrentTime: () => number | undefined | Promise<number>

  /**
   * Return the total duration of the video in seconds.
   */
  getDuration: () => number | undefined | Promise<number>

  /**
   * Return the canonical video URL.
   */
  getVideoUrl: () => string | undefined | Promise<string>
}

// The current player state.
type State = 'loaded' | 'playing' | 'paused' | 'ended'

type UseVideoTracking = {
  /**
   * Set the current state of the video.
   */
  setState: (state: State) => void
}

/**
 * Returns the percentage video progress for use in analytics tracking.
 *
 * Since tracking should only start at 10%, the method returns null
 * when the percentage would be 0.
 *
 * Once the last 95% has been reached the method returns 100%.
 *
 * @returns The percentage.
 */
function getVideoProgress(
  currentTime: number,
  duration: number,
): string | null {
  const progress = (currentTime / duration) * 100

  if (progress < 10) {
    return null
  }

  return (progress >= 95 ? 100 : Math.floor(progress / 10) * 10).toString()
}

/**
 * Handles tracking of video player events.
 */
export default function (
  /**
   * The video provider.
   */
  provider: 'vimeo' | 'youtube',

  /**
   * The adapter to get the state of the player.
   */
  adapter: UseVideoTrackingAdapter,
): UseVideoTracking {
  const { trackEvent } = useAnalytics()
  const state = ref<State | null>(null)

  // Keep track of the percentages that have been tracked so far.
  const trackedPercentages: Set<string> = new Set()

  /**
   * Track a video event.
   */
  async function trackVideoEvent(name: string) {
    const currentTime = await adapter.getCurrentTime()
    const duration = await adapter.getDuration()
    const videoTitle = await adapter.getTitle()
    const url = await adapter.getVideoUrl()

    trackEvent({
      event: name,
      video_title: videoTitle,
      video_url: url,
      video_duration: duration,
      video_current_time: currentTime ? Math.round(currentTime) : 0,
      video_provider: provider,
      video_percent:
        currentTime && duration
          ? getVideoProgress(currentTime, duration) || 0
          : 0,
    })
  }

  /**
   * Set the state.
   */
  function setState(newState: State) {
    if (newState === 'paused') {
      // Pause the interval function.
      pauseInterval()
    } else if (newState === 'ended') {
      trackVideoEvent('video_complete')
      // Pause the interval function.
      pauseInterval()
    } else if (newState === 'playing') {
      // Only track this event when we are not already playing.
      if (state.value !== 'playing') {
        trackVideoEvent('video_start')
      }

      resumeInterval()
    }
    state.value = newState
  }

  const { pause: pauseInterval, resume: resumeInterval } = useIntervalFn(
    async () => {
      const currentTime = await adapter.getCurrentTime()
      const duration = await adapter.getDuration()

      if (!currentTime || !duration) {
        return
      }

      const progressRounded = getVideoProgress(currentTime, duration)

      // This is the case when the percentage is < 10.
      if (!progressRounded) {
        return
      }

      // Don't track if the progress has already been tracked.
      if (trackedPercentages.has(progressRounded)) {
        return
      }

      trackedPercentages.add(progressRounded)
      trackVideoEvent('video_progress')
    },
    1000,
    {
      // Don't start the interval on mounted.
      immediate: false,
    },
  )

  return {
    setState,
  }
}
